import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { radius, helpDark } from '../../themes/smartinventoryTheme';
import { useHelpBundle } from './helpBundle';
import eventBus from '../../utils/eventBus';

const STEP_CARD_WIDTH = 320;

export const tourSeenKey = (route) => `help.tour.seen.${route}`;
export const hasSeenTour = (route) => {
  try { return localStorage.getItem(tourSeenKey(route)) === 'true'; } catch { return false; }
};
export const markTourSeen = (route) => {
  try { localStorage.setItem(tourSeenKey(route), 'true'); } catch { /* private-mode storage — non-fatal, just replays next visit */ }
};

// Re-measures the target element on mount/resize/scroll and shortly after
// (steps often target something that finishes animating in) — the tour
// steps through content, not fixed coordinates, so the spotlight has to
// track wherever that content currently sits.
const useTargetRect = (selector) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const update = () => {
      if (cancelled) return;
      const el = selector ? document.querySelector(selector) : null;
      setRect(el ? el.getBoundingClientRect() : null);
    };
    update();
    const retry = setTimeout(update, 80);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelled = true;
      clearTimeout(retry);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [selector]);
  return rect;
};

// Layer 3 (step 9): numbered spotlight walkthrough. `route` selects
// `help.json` tours.<route>.steps[]; each step is `{ selector, title, body }`
// naming a real element already on the page (see the `data-tour="..."`
// attributes added alongside this). Disabled below 900px per the design —
// the tour card in the panel reads "Read the guide" there instead.
const HelpTour = ({ route, onClose }) => {
  const { t } = useTranslation('help');
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const bundle = useHelpBundle();
  const steps = bundle.tours?.[route]?.steps || [];
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const rect = useTargetRect(step?.selector);
  const cardRef = useRef(null);

  useEffect(() => { if (route) markTourSeen(route); }, [route]);
  useEffect(() => { setIndex(0); }, [route]);

  useEffect(() => {
    if (!step) return;
    eventBus.emit(eventBus.EVENTS.HELP_TOUR_STEP, { route, step: index + 1, total: steps.length });
  }, [route, index, step, steps.length]);

  // Focus moves into the card on mount and each step change; Tab/Shift+Tab
  // cycle between the card's two buttons instead of escaping to the page
  // underneath the overlay.
  useEffect(() => {
    const focusable = cardRef.current?.querySelectorAll('button');
    focusable?.[0]?.focus();
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = cardRef.current ? Array.from(cardRef.current.querySelectorAll('button')) : [];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (isNarrow || !steps.length || !step) return null;

  const isLast = index === steps.length - 1;
  const cardTop = rect
    ? Math.min(Math.max(rect.bottom + 16, 16), window.innerHeight - 220)
    : window.innerHeight / 2 - 110;
  const cardLeft = rect
    ? Math.min(Math.max(rect.left, 16), window.innerWidth - STEP_CARD_WIDTH - 16)
    : window.innerWidth / 2 - STEP_CARD_WIDTH / 2;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
      sx={{ position: 'fixed', inset: 0, zIndex: (th) => th.zIndex.modal + 10, bgcolor: helpDark.overlay }}
    >
      {rect && (
        <Box
          data-testid="help-tour-spotlight"
          sx={{
            position: 'fixed',
            top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8,
            borderRadius: `${radius.sm}px`, pointerEvents: 'none',
            boxShadow: (th) => `0 0 0 4px ${th.palette.primary.main}, 0 0 0 9px ${alpha(th.palette.primary.main, 0.25)}`,
          }}
        />
      )}
      <Box
        ref={cardRef}
        sx={{
          position: 'fixed', top: cardTop, left: cardLeft, width: STEP_CARD_WIDTH,
          bgcolor: 'background.paper', borderRadius: `${radius.lg}px`, p: 2, boxShadow: 6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{
            width: 22, height: 22, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}
          >
            {index + 1}
          </Box>
          <Typography variant="subtitle1">{step.title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{step.body}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button size="small" onClick={onClose}>{t('tour.skip', { defaultValue: 'Skip tour' })}</Button>
          <Typography variant="caption" color="text.secondary">{t('tour.stepOf', { step: index + 1, total: steps.length, defaultValue: `${index + 1} / ${steps.length}` })}</Typography>
          <Button size="small" variant="contained" onClick={() => (isLast ? onClose() : setIndex((i) => i + 1))}>
            {isLast ? t('tour.done', { defaultValue: 'Done' }) : t('tour.next', { defaultValue: 'Next' })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default HelpTour;
